import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../lib/api';
import { formatEuro, LABEL_TYPE_CLIENT } from '../../lib/format';
import { ChartEmpty } from './ChartEmpty';
import type { DashboardFiltersState } from './filters';
import { presetToBounds } from './filters';

interface Props {
  filters: DashboardFiltersState;
}

export function ChartCAByClientType({ filters }: Props): JSX.Element {
  const bounds = presetToBounds(filters);
  const params = {
    period_from: bounds.period_from,
    period_to: bounds.period_to,
    user_id: filters.user_id,
  };
  const query = useQuery({
    queryKey: ['dashboard', 'ca-by-client-type', params],
    queryFn: () => api.dashboard.caByClientType(params),
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <Shell>
        <div className="h-[220px] bg-slate-800/40 rounded animate-pulse" />
      </Shell>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <Shell>
        <ChartEmpty message="Erreur de chargement" />
      </Shell>
    );
  }
  const total = query.data.rows.reduce((s, r) => s + r.ca_ht_centimes, 0);
  if (total === 0) {
    return (
      <Shell>
        <ChartEmpty />
      </Shell>
    );
  }
  const chartData = query.data.rows.map((r) => ({
    label: LABEL_TYPE_CLIENT[r.type_client] ?? r.type_client,
    ca: r.ca_ht_centimes / 100,
    nb: r.nb_ventes,
  }));
  return (
    <Shell>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#334155" />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            stroke="#334155"
            tickFormatter={(v: number): string =>
              v >= 1000 ? `${(v / 1000).toFixed(0)} k€` : `${String(v)} €`
            }
          />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
            labelStyle={{ color: '#e2e8f0' }}
            formatter={(
              value: number,
              _name: string,
              item: { payload?: { nb?: number } },
            ): [string, string] => {
              const nb = item.payload?.nb ?? 0;
              return [`${formatEuro(value * 100)} · ${String(nb)} ventes`, ''];
            }}
          />
          <Bar dataKey="ca" fill="#1f7a3d" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
        CA par type de client
      </div>
      {children}
    </div>
  );
}
